import React from 'react';
import {
  CheckCircle,
  Info,
  Lightbulb,
  ChevronRight,
  Send,
  Settings,
  MessageCircle,
  Zap,
  BarChart3,
  Search,
  Globe,
  Users,
} from 'lucide-react';

// Reusable components from ManagingDashboard
const ExampleBox = ({ children }) => (
  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 rounded-r-lg">
    <div className="flex items-start">
      <Info className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-sm font-semibold text-blue-800 mb-1">Example</p>
        <div className="text-blue-700 text-sm">{children}</div>
      </div>
    </div>
  </div>
);

const ProTip = ({ children }) => (
  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded-r-lg">
    <div className="flex items-start">
      <Lightbulb className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-sm font-semibold text-yellow-800 mb-1">Pro Tip</p>
        <div className="text-yellow-700 text-sm">{children}</div>
      </div>
    </div>
  </div>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="url(#instagram-gradient)">
    <defs>
      <linearGradient id="instagram-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f09433" />
        <stop offset="25%" stopColor="#e6683c" />
        <stop offset="50%" stopColor="#dc2743" />
        <stop offset="75%" stopColor="#cc2366" />
        <stop offset="100%" stopColor="#bc1888" />
      </linearGradient>
    </defs>
    <path d="M16.98 0a6.9 6.9 0 0 1 6.9 6.9v10.2a6.9 6.9 0 0 1-6.9 6.9H7.02a6.9 6.9 0 0 1-6.9-6.9V6.9A6.9 6.9 0 0 1 7.02 0h9.96zM12 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#25D366">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.525 3.488" />
  </svg>
);

const QuickReply = () => {
  return (
    <div className="min-h-screen bg-gray-50 mt-[32px]">
      
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="bg-purple-100 rounded-lg p-2 mr-4">
              <MessageCircle className="w-6 h-6 text-purple-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Quick Reply: Simplify and Speed Up Your Responses
            </h1>
          </div>
          <p className="text-gray-600 mb-6">
            The Quick Reply feature is designed to make responding to customer messages faster, easier, and more consistent. Create predefined responses for frequently asked questions or common scenarios to save time and maintain a professional tone.
          </p>

          {/* What Are Quick Replies */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">What Are Quick Replies?</h2>
            <p className="text-gray-600 mb-4">
              Quick Replies are pre-written messages that you can insert into conversations with a few clicks or keystrokes. They’re ideal for:
            </p>
            <ul className="space-y-2 text-gray-600">
              {[
                'Responding to common questions like operating hours, pricing, or services offered.',
                'Sending acknowledgment messages like “Thank you for reaching out!”',
                'Sharing promotional offers or links quickly.',
              ].map((item, index) => (
                <li key={index} className="flex items-start">
                  <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-purple-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <ExampleBox>
              Send a quick response like: “Thank you for your inquiry! We’ll get back to you within 24 hours.”
            </ExampleBox>
          </section>

          {/* Simple Setup */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">1. Simple Setup</h2>
            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Create Quick Replies Effortlessly</h3>
              <ol className="space-y-3 text-gray-600">
                {[
                  'Navigate to the <strong>Quick Replies</strong> section in your account settings.',
                  'Click <strong>Add New Quick Reply</strong>, give it a title (e.g., “Welcome Message”), and type the message.',
                  'Save the Quick Reply, and it’s ready to use.',
                ].map((step, index) => (
                  <li key={index} className="flex items-start">
                    <span className="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">
                      {index + 1}
                    </span>
                    <span dangerouslySetInnerHTML={{ __html: step }} />
                  </li>
                ))}
              </ol>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Example Messages</h3>
            <ul className="space-y-2 text-gray-600">
              {[
                '"Thank you for your inquiry! We’ll get back to you shortly."',
                '"Our business hours are Monday to Friday, 9 AM to 6 PM."',
                '"Click here to track your order: [insert link]."',
              ].map((item, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle className="w-4 h-4 mt-0.5 mr-2 text-green-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Real-Time Usability */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">2. Real-Time Usability</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Send className="w-5 h-5 mr-2 text-purple-600" />
                  Instant Access in Chats
                </h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    'While replying to a message, type <strong>/</strong> to display a dropdown menu of all your Quick Replies.',
                    'Scroll through or search for the desired Quick Reply by typing its title or keywords.',
                    'Select the Quick Reply to instantly populate it in the message input box.',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-purple-500" />
                      <span dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Globe className="w-5 h-5 mr-2 text-blue-600" />
                  Platform-Specific Flexibility
                </h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    'Use Quick Replies seamlessly across all connected platforms—WhatsApp, Instagram, Facebook, and Telegram.',
                    'Customize platform-specific replies if needed (e.g., "DM us on Instagram for more info!" vs. "Reply to this WhatsApp message for assistance.").',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-blue-500" />
                      <span dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  ))}
                </ul>
                <div className="flex space-x-2 mt-4">
                  <FacebookIcon />
                  <InstagramIcon />
                  <WhatsAppIcon />
                </div>
              </div>
            </div>
            <ProTip>
              Create platform-specific Quick Replies to tailor your tone and content to each platform’s audience for a more personalized experience.
            </ProTip>
          </section>

          {/* Easy Management */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">3. Easy Management</h2>
            <div className="bg-gray-50 rounded-lg p-6 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <Settings className="w-5 h-5 mr-2 text-gray-600" />
                Edit or Delete Quick Replies
              </h3>
              <ul className="space-y-2 text-gray-600">
                {[
                  'Update a message by navigating to <strong>Quick Replies</strong> settings, selecting the reply, making changes, and saving.',
                  'Remove outdated messages with a single click.',
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-gray-500" />
                    <span dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Categorize Quick Replies</h3>
            <ul className="space-y-2 text-gray-600">
              <li className="flex items-start">
                <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-gray-500" />
                Group similar replies (e.g., Support, Sales, Promotions) for easy navigation.
              </li>
            </ul>
          </section>

          {/* How to Use Quick Replies */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">How to Use Quick Replies in Conversations</h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">While Responding to a Message</h3>
                <ol className="space-y-3 text-gray-600">
                  {[
                    'Open a chat window from any platform (e.g., WhatsApp, Instagram).',
                    'Type <strong>/</strong> to bring up the Quick Reply menu.',
                    'Search or scroll to find the appropriate reply.',
                    'Click on the Quick Reply to insert it into the message box.',
                    'Customize the message further (if needed) before hitting Send.',
                  ].map((step, index) => (
                    <li key={index} className="flex items-start">
                      <span className="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">
                        {index + 1}
                      </span>
                      <span dangerouslySetInnerHTML={{ __html: step }} />
                    </li>
                  ))}
                </ol>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">When Sending Promotional Campaigns</h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    'Use Quick Replies to send consistent promotional messages to multiple customers.',
                    'Example: “We’re offering 20% off this week! Shop now: [insert link].”',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-blue-500" />
                      <span dangerouslySetInnerHTML={{ __html: item }} />
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">During High-Volume Chats</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-green-500" />
                    Handle peak hours with ease by using Quick Replies for common inquiries, reducing typing effort and response time.
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Benefits of Quick Replies</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: 'Save Time', description: 'No need to type the same response repeatedly—just click and send.' },
                { title: 'Ensure Consistency', description: 'Maintain a uniform tone and message quality across all interactions.' },
                { title: 'Boost Productivity', description: 'Respond to more customers in less time, especially during busy hours.' },
                { title: 'Enhance Customer Experience', description: 'Quick responses lead to higher customer satisfaction and engagement.' },
              ].map((benefit, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </div>
              ))}
            </div>
            <ProTip>
              Regularly review your Quick Replies to ensure they align with your brand voice and customer needs for maximum effectiveness.
            </ProTip>
          </section>

          {/* Advanced Features */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Advanced Quick Reply Features</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Zap className="w-5 h-5 mr-2 text-emerald-600" />
                  Smart Suggestions
                </h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    'ChatInject’s AI suggests relevant Quick Replies based on the context of the customer’s message.',
                    'Example: If a customer asks, “What are your business hours?” the system will automatically suggest your predefined hours reply.',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-emerald-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-blue-600" />
                  User-Specific Replies
                </h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    'Tailor Quick Replies to specific user groups.',
                    'Example: VIP customers can receive personalized replies like, “As a valued VIP member, here’s your exclusive discount code: VIP2024.”',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-blue-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-purple-600" />
                  Analytics for Quick Replies
                </h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    'Track the usage of Quick Replies to identify frequently asked questions.',
                    'Adjust your predefined messages to align with common customer needs and improve communication.',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-purple-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <ExampleBox>
              Use analytics to discover that 30% of inquiries are about business hours, then create a Quick Reply to address this efficiently.
            </ExampleBox>
          </section>

          {/* How to Set Up */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">How to Set Up Quick Replies</h2>
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Settings className="w-5 h-5 mr-2 text-gray-600" />
                  Navigate to Quick Replies Settings
                </h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start">
                    <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-gray-500" />
                    Open your dashboard and click on <strong>Settings → Quick Replies</strong>.
                  </li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Add a New Quick Reply</h3>
                <ol className="space-y-3 text-gray-600">
                  {[
                    'Click the <strong>Add New</strong> button.',
                    'Enter a <strong>Title</strong> to identify the Quick Reply.',
                    'Write the message content.',
                    'Choose the platforms (e.g., Instagram, Facebook) where this Quick Reply should be available.',
                    'Save the reply.',
                  ].map((step, index) => (
                    <li key={index} className="flex items-start">
                      <span className="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 mt-0.5">
                        {index + 1}
                      </span>
                      <span dangerouslySetInnerHTML={{ __html: step }} />
                    </li>
                  ))}
                </ol>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Organize and Manage Replies</h3>
                <ul className="space-y-2 text-gray-600">
                  {[
                    'Use categories to organize your replies (e.g., Support, Marketing, General).',
                    'Reorder replies for faster access to frequently used ones.',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <ChevronRight className="w-4 h-4 mt-0.5 mr-2 text-gray-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <ProTip>
              Categorize Quick Replies by department or use case to streamline access for team members handling specific tasks.
            </ProTip>
          </section>

          {/* Footer */}
          <footer className="bg-white rounded-lg shadow-sm p-6 text-center">
            <p className="text-gray-600 mb-4">
              Need help with Quick Replies? Our support team is here to assist you with setup and optimization.
            </p>
            <div className="flex justify-center space-x-4 text-sm">
              <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Support Center</a>
              <span className="text-gray-300">|</span>
              <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Video Tutorials</a>
              <span className="text-gray-300">|</span>
              <a href="#" className="text-blue-600 hover:text-blue-800 transition-colors">Contact Us</a>
            </div>
          </footer>
        </div>
    </div>
  );
};

export default QuickReply;